
import { getTourPriceOverride } from "@/lib/tourDurationPricing";

interface ServiceItem {
  free_of_charge?: boolean;
  is_selected_by_client?: boolean;
  foc_quantity?: number;
  ticket_quantity?: number;
  time_from?: string | null;
  time_to?: string | null;
  price_override?: number | string | null;
  tours?: PricedService;
  attractions?: PricedService;
  attraction_tickets?: PricedService;
  transfers?: PricedService;
  airport_services?: PricedService;
  hotel_rooms?: HotelRoom;
  number_of_rooms?: number;
  meal_option?: string;
  insurance_price?: number;
  request_price?: number;
  request_quantity?: number;
  booking_with_us?: boolean;
  booking_price?: number | string | null;
  hcte_night_rate?: number | string | null;
  accommodation_website_night_rate?: number | string | null;
  booking_com_night_rate?: number | string | null;
}

interface PricedService {
  price_op?: number;
  price_ag?: number;
  price_cf?: number;
  duration_hours?: number;
  tax_percentage?: number | string | null;
  pricing_type?: 'per_person' | 'per_family' | 'per_group';
}

interface HotelRoom {
  price_room_only?: number;
  price_breakfast?: number;
  price_half_board?: number;
  price_full_board?: number;
}

interface HandlingFee {
  fee_price: number;
}

interface ProposalData {
  pax_adults?: number;
  pax_children?: number;
  pax_infants?: number;
  pricing_adjustment_type?: PricingAdjustmentType | string | null;
  pricingAdjustmentType?: PricingAdjustmentType | string | null;
  pricing_adjustment_percentage?: number | string | null;
  pricingAdjustmentPercentage?: number | string | null;
  price_tier_override?: "operator" | "agency" | "final" | string | null;
  priceTierOverride?: "operator" | "agency" | "final" | string | null;
  vat_exempt?: boolean | null;
  vatExempt?: boolean | null;
  public_pricing_view?: ProposalPricingView | string | null;
  publicPricingView?: ProposalPricingView | string | null;
  clients?: {
    partner_business_type?: "agency" | "agent" | "operator" | null;
    partnerBusinessType?: "agency" | "agent" | "operator" | null;
    partner_id?: number | null;
    partnerId?: number | null;
  };
  proposal_tours?: ServiceItem[];
  proposal_attractions?: ServiceItem[];
  proposal_transfers?: ServiceItem[];
  proposal_flights?: ServiceItem[];
  proposal_airport_services?: ServiceItem[];
  proposal_accommodations?: ServiceItem[];
  proposal_additional_requests?: ServiceItem[];
  proposal_insurance?: ServiceItem[];
  proposal_handling_fee?: HandlingFee[];
}

interface FinancialBreakdown {
  flights: { count: number; base: number; taxes: number; payable: number };
  transfers: { count: number; base: number; taxes: number; payable: number };
  tours: { count: number; base: number; taxes: number; payable: number };
  attractions: { count: number; base: number; taxes: number; payable: number };
  accommodations: { count: number; base: number; taxes: number; payable: number };
  airport: { count: number; base: number; taxes: number; payable: number };
  additionalRequests: { count: number; base: number; taxes: number; payable: number };
  insurance: { count: number; base: number; taxes: number; payable: number };
  handlingFee: {
    base: number;
    taxes: number;
    payable: number;
  };
  totalBase: number;
  totalTaxes: number;
  totalPayable: number;
  totalBaseBeforeAdjustment: number;
  totalTaxesBeforeAdjustment: number;
  totalPayableBeforeAdjustment: number;
  pricingAdjustmentType: PricingAdjustmentType;
  pricingAdjustmentPercentage: number;
  pricingAdjustmentAmount: number;
  pricingAdjustmentTaxAmount: number;
  pricingAdjustmentTotalImpact: number;
}

export type PricingAdjustmentType = "none" | "commission" | "discount";
export type ProposalPricingView = "adjusted" | "base";

const round2 = (value: number): number => {
  return Math.round(value * 100) / 100;
};


/**
 * A commission is unbounded — sales sets whatever markup the deal takes. A discount is not:
 * past 100% it stops reducing the total and starts inverting it. Mirrors the backend's
 * MAX_DISCOUNT_PERCENTAGE.
 */
export const MAX_DISCOUNT_PERCENTAGE = 100;

export const normalizePricingAdjustmentType = (value: unknown): PricingAdjustmentType => {
  const normalized = (value || "").toString().trim().toLowerCase();
  if (normalized === "commission" || normalized === "discount") {
    return normalized;
  }
  return "none";
};

export const normalizePricingAdjustmentPercentage = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  // Free-form magnitude, matching the backend: only the sign is constrained, since the
  // direction of the adjustment comes from pricing_adjustment_type.
  const rounded = round2(parsed);
  return rounded > 0 ? rounded : 0;
};

export const resolveProposalPricingView = (
  proposal: Pick<ProposalData, "public_pricing_view" | "publicPricingView"> | null | undefined,
  override?: ProposalPricingView | string | null,
): ProposalPricingView => {
  const raw = override ?? proposal?.public_pricing_view ?? proposal?.publicPricingView;
  return raw === "base" ? "base" : "adjusted";
};

export const getProposalPricingAdjustment = (
  proposal: ProposalData | null | undefined,
  pricingView?: ProposalPricingView | string | null,
): { type: PricingAdjustmentType; percentage: number; factor: number } => {
  const type = normalizePricingAdjustmentType(
    proposal?.pricing_adjustment_type ?? proposal?.pricingAdjustmentType
  );
  const rawPercentage = normalizePricingAdjustmentPercentage(
    proposal?.pricing_adjustment_percentage ?? proposal?.pricingAdjustmentPercentage
  );
  // Guards legacy rows written before the discount ceiling existed, so an old 250% discount
  // reads as 100% instead of flipping the sign of every total on the proposal.
  const percentage =
    type === "discount" ? Math.min(rawPercentage, MAX_DISCOUNT_PERCENTAGE) : rawPercentage;
  const view = resolveProposalPricingView(proposal, pricingView);
  if (view === "base" || type === "none" || percentage === 0) {
    return { type: "none", percentage: 0, factor: 1 };
  }
  const rate = percentage / 100;
  return {
    type,
    percentage,
    factor: type === "discount" ? round2(1 - rate) : round2(1 + rate),
  };
};

export const applyProposalPricingAdjustment = (
  value: number | string | null | undefined,
  proposal: ProposalData | null | undefined,
  options?: { pricingView?: ProposalPricingView | string | null; exempt?: boolean },
): number => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  if (options?.exempt) return round2(parsed);
  const adjustment = getProposalPricingAdjustment(proposal, options?.pricingView);
  return round2(parsed * adjustment.factor);
};

const USD_TO_AED_RATE = 3.6725;

const normalizeCurrencyCode = (currency: string | null | undefined): string => {
  const normalized = (currency || "").toString().trim().toUpperCase();
  return normalized === "AED" ? "AED" : "USD";
};

export const convertUsdToCurrency = (value: number | null | undefined, currency: string): number => {
  const safe = !value || Number.isNaN(value) ? 0 : value;
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (normalizedCurrency === "AED") {
    return round2(safe * USD_TO_AED_RATE);
  }
  return safe;
};

const getClientPriceKey = (partnerBusinessType?: string): "price_cf" | "price_ag" | "price_op" => {
  switch (partnerBusinessType) {
    case 'agency':
    case 'agent':
      return 'price_ag';
    case 'operator':
      return 'price_op';
    default:
      return 'price_cf';
  }
};

// Resolves the effective price tier: a proposal-level override
// ("operator" | "agency" | "final") wins over the tier derived from the
// client's partner. "final"/"auto"/blank fall through to price_cf via getClientPriceKey.
const resolveEffectiveTier = (proposal: {
  price_tier_override?: string | null;
  priceTierOverride?: string | null;
  clients?: { partner_business_type?: string | null; partnerBusinessType?: string | null } | null;
} | null | undefined): string | undefined => {
  const override = (proposal?.price_tier_override ?? proposal?.priceTierOverride ?? null)
    ?.toString()
    .toLowerCase();
  if (override && override !== "auto") {
    return override;
  }
  const partnerBusinessType = (
    proposal?.clients?.partner_business_type ??
    proposal?.clients?.partnerBusinessType ??
    null
  )?.toString().toLowerCase();
  return partnerBusinessType || undefined;
};

const getPrice = (service: PricedService | undefined, priceKey: string): number => {
  if (!service) return 0;
  return parseFloat(String(service[priceKey as keyof PricedService] || 0));
};

const getTaxPercentage = (service: PricedService | undefined): number => {
  if (!service) return 0;
  const tax = parseFloat(String(service.tax_percentage ?? 0));
  return Number.isFinite(tax) && tax > 0 ? tax : 0;
};

const getAttractionTaxPercentage = (item: ServiceItem): number => {
  const ticketTax = getTaxPercentage(item.attraction_tickets);
  if (ticketTax > 0) {
    return ticketTax;
  }
  return getTaxPercentage(item.attractions);
};

const toTaxedAmount = (baseAmount: number, taxPercentage: number): { base: number; taxes: number; payable: number } => {
  const safeBase = Number.isFinite(baseAmount) ? baseAmount : 0;
  if (safeBase <= 0) return { base: 0, taxes: 0, payable: 0 };
  const normalizedBase = round2(safeBase);
  const normalizedTax = Number.isFinite(taxPercentage) && taxPercentage > 0
    ? round2((normalizedBase * taxPercentage) / 100)
    : 0;
  return {
    base: normalizedBase,
    taxes: normalizedTax,
    payable: round2(normalizedBase + normalizedTax),
  };
};

const parseOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getAccommodationNights = (checkIn: string | null | undefined, checkOut: string | null | undefined): number => {
  if (!checkIn || !checkOut) return 1;
  const inDate = new Date(checkIn);
  const outDate = new Date(checkOut);
  if (Number.isNaN(inDate.getTime()) || Number.isNaN(outDate.getTime())) return 1;
  const diff = outDate.getTime() - inDate.getTime();
  const nights = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return Math.max(1, nights);
};

const calculateServiceTotals = (
  item: ServiceItem,
  service: PricedService | undefined,
  priceKey: string,
  paxCount: number,
  adjustmentFactor: number,
  taxPercentageOverride?: number
) => {
  if (item.free_of_charge) return { base: 0, taxes: 0, payable: 0 };

  if (item.is_selected_by_client === false) return { base: 0, taxes: 0, payable: 0 };

  const overridePrice = item.tours ? getTourPriceOverride(item) : null;
  if (!service && overridePrice === null) return { base: 0, taxes: 0, payable: 0 };

  const unitPrice = overridePrice ?? getPrice(service, priceKey);

  if (unitPrice === 0) return { base: 0, taxes: 0, payable: 0 };

  let quantity = 1;
  if (service.pricing_type === 'per_person') {
    quantity = paxCount - (item.foc_quantity || 0);
    quantity = Math.max(0, quantity);
  }
  
  if (item.ticket_quantity !== undefined) {
    quantity = item.ticket_quantity - (item.foc_quantity || 0);
    quantity = Math.max(0, quantity);
  }

  const baseAmount = round2(unitPrice * quantity * adjustmentFactor);
  const taxPercentage = typeof taxPercentageOverride === "number"
    ? taxPercentageOverride
    : getTaxPercentage(service);
  return toTaxedAmount(baseAmount, taxPercentage);
};

const computeProposalFinancialSummaryForFactor = (proposal: ProposalData, adjustmentFactor: number): Omit<
  FinancialBreakdown,
  | "totalBaseBeforeAdjustment"
  | "totalTaxesBeforeAdjustment"
  | "totalPayableBeforeAdjustment"
  | "pricingAdjustmentType"
  | "pricingAdjustmentPercentage"
  | "pricingAdjustmentAmount"
  | "pricingAdjustmentTaxAmount"
  | "pricingAdjustmentTotalImpact"
> => {
  const priceKey = getClientPriceKey(resolveEffectiveTier(proposal));
  const paxCountRaw = (proposal.pax_adults || 0) + (proposal.pax_children || 0) + (proposal.pax_infants || 0);
  const paxCount = Math.max(1, paxCountRaw);

  // VAT-exempt proposals (e.g. destinations outside the UAE) drop all VAT: both the
  // fixed 5% (accommodation/additional requests/insurance/handling fee) and the variable
  // per-item tax percentages (tours/attractions/transfers/airport services).
  // Mirrors ProposalService#calculateTotalValue (backend).
  const isVatExempt = Boolean(proposal.vat_exempt ?? proposal.vatExempt);
  const fixedTaxRate = isVatExempt ? 0 : 5;

  let toursBase = 0;
  let toursTaxes = 0;
  let toursPayable = 0;
  (proposal.proposal_tours || []).forEach(tour => {
    const totals = calculateServiceTotals(tour, tour.tours, priceKey, paxCount, adjustmentFactor, isVatExempt ? 0 : undefined);
    toursBase += totals.base;
    toursTaxes += totals.taxes;
    toursPayable += totals.payable;
  });

  let attractionsBase = 0;
  let attractionsTaxes = 0;
  let attractionsPayable = 0;
  (proposal.proposal_attractions || []).forEach(attr => {
    const totals = calculateServiceTotals(
      attr,
      attr.attraction_tickets,
      priceKey,
      paxCount,
      adjustmentFactor,
      isVatExempt ? 0 : getAttractionTaxPercentage(attr),
    );
    attractionsBase += totals.base;
    attractionsTaxes += totals.taxes;
    attractionsPayable += totals.payable;
  });

  let transfersBase = 0;
  let transfersTaxes = 0;
  let transfersPayable = 0;
  (proposal.proposal_transfers || []).forEach(transfer => {
    const totals = calculateServiceTotals(transfer, transfer.transfers, priceKey, paxCount, adjustmentFactor, isVatExempt ? 0 : undefined);
    transfersBase += totals.base;
    transfersTaxes += totals.taxes;
    transfersPayable += totals.payable;
  });

  let airportBase = 0;
  let airportTaxes = 0;
  let airportPayable = 0;
  (proposal.proposal_airport_services || []).forEach(service => {
    const totals = calculateServiceTotals(service, service.airport_services, priceKey, paxCount, adjustmentFactor, isVatExempt ? 0 : undefined);
    airportBase += totals.base;
    airportTaxes += totals.taxes;
    airportPayable += totals.payable;
  });

  let flightsBase = 0;
  let flightsTaxes = 0;
  let flightsPayable = 0;
  (proposal.proposal_flights || []).forEach((flight) => {
    if (flight.is_selected_by_client === false) return;
    if (!flight.booking_with_us) return;
    const bookingPrice = parseOptionalNumber(flight.booking_price);
    if (bookingPrice === null || bookingPrice <= 0) return;
    const totals = toTaxedAmount(round2(bookingPrice * adjustmentFactor), 0);
    flightsBase += totals.base;
    flightsTaxes += totals.taxes;
    flightsPayable += totals.payable;
  });

  let accommodationsBase = 0;
  let accommodationsTaxes = 0;
  let accommodationsPayable = 0;
  (proposal.proposal_accommodations || []).forEach((acc: any) => {
    if (acc.is_selected_by_client === false) return;
    
    if (acc.free_of_charge) return;
    
    if (acc.to_be_quoted) return;
    
    const hcteNightRate = parseOptionalNumber(acc.hcte_night_rate ?? acc.booking_price);
    if (acc.booking_with_us && hcteNightRate !== null && hcteNightRate > 0) {
      const rooms = Math.max(1, Number(acc.number_of_rooms || 1));
      const nights = getAccommodationNights(acc.check_in, acc.check_out);
      const totals = toTaxedAmount(round2(hcteNightRate * rooms * nights * adjustmentFactor), fixedTaxRate);
      accommodationsBase += totals.base;
      accommodationsTaxes += totals.taxes;
      accommodationsPayable += totals.payable;
      return;
    }
    
    if (acc.booking_with_us && acc.hotel_rooms) {
      const mealPriceKey = 
        acc.meal_option === 'BB' ? 'price_breakfast' :
        acc.meal_option === 'HB' ? 'price_half_board' :
        acc.meal_option === 'FB' ? 'price_full_board' : 
        acc.meal_option === 'AI' ? 'price_room_only' :
        'price_breakfast';
      
      const roomPrice = parseFloat(String(acc.hotel_rooms[mealPriceKey] || 0));
      const rooms = acc.number_of_rooms || 1;
      const nights = getAccommodationNights(acc.check_in, acc.check_out);
      const totals = toTaxedAmount(round2(roomPrice * rooms * nights * adjustmentFactor), fixedTaxRate);
      accommodationsBase += totals.base;
      accommodationsTaxes += totals.taxes;
      accommodationsPayable += totals.payable;
    }
  });

  let additionalRequestsBase = 0;
  let additionalRequestsTaxes = 0;
  let additionalRequestsPayable = 0;
  (proposal.proposal_additional_requests || []).forEach(req => {
    if (req.is_selected_by_client !== false) {
      const quantity = Math.max(1, Number(req.request_quantity || 1));
      const price = parseFloat(String(req.request_price || 0)) * quantity;
      const totals = toTaxedAmount(round2(price * adjustmentFactor), fixedTaxRate);
      additionalRequestsBase += totals.base;
      additionalRequestsTaxes += totals.taxes;
      additionalRequestsPayable += totals.payable;
    }
  });

  let insuranceBase = 0;
  let insuranceTaxes = 0;
  let insurancePayable = 0;
  (proposal.proposal_insurance || []).forEach(ins => {
    if (ins.is_selected_by_client !== false) {
      const price = parseFloat(String(ins.insurance_price || 0));
      const totals = toTaxedAmount(round2(price * adjustmentFactor), fixedTaxRate);
      insuranceBase += totals.base;
      insuranceTaxes += totals.taxes;
      insurancePayable += totals.payable;
    }
  });

  let handlingBase = 0;
  let handlingTaxes = 0;
  let handlingPayable = 0;
  (proposal.proposal_handling_fee || []).forEach(fee => {
    const feePrice = parseFloat(String(fee.fee_price || 0));
    const totals = toTaxedAmount(feePrice, fixedTaxRate);
    handlingBase += totals.base;
    handlingTaxes += totals.taxes;
    handlingPayable += totals.payable;
  });
  handlingBase = round2(handlingBase);
  handlingTaxes = round2(handlingTaxes);
  handlingPayable = round2(handlingPayable);

  toursBase = round2(toursBase);
  toursTaxes = round2(toursTaxes);
  toursPayable = round2(toursPayable);
  attractionsBase = round2(attractionsBase);
  attractionsTaxes = round2(attractionsTaxes);
  attractionsPayable = round2(attractionsPayable);
  transfersBase = round2(transfersBase);
  transfersTaxes = round2(transfersTaxes);
  transfersPayable = round2(transfersPayable);
  airportBase = round2(airportBase);
  airportTaxes = round2(airportTaxes);
  airportPayable = round2(airportPayable);
  flightsBase = round2(flightsBase);
  flightsTaxes = round2(flightsTaxes);
  flightsPayable = round2(flightsPayable);
  accommodationsBase = round2(accommodationsBase);
  accommodationsTaxes = round2(accommodationsTaxes);
  accommodationsPayable = round2(accommodationsPayable);
  additionalRequestsBase = round2(additionalRequestsBase);
  additionalRequestsTaxes = round2(additionalRequestsTaxes);
  additionalRequestsPayable = round2(additionalRequestsPayable);
  insuranceBase = round2(insuranceBase);
  insuranceTaxes = round2(insuranceTaxes);
  insurancePayable = round2(insurancePayable);

  const totalBase = round2(
    toursBase +
    attractionsBase +
    transfersBase +
    flightsBase +
    airportBase +
    accommodationsBase +
    additionalRequestsBase +
    insuranceBase +
    handlingBase
  );

  const totalTaxes = round2(
    toursTaxes +
    attractionsTaxes +
    transfersTaxes +
    flightsTaxes +
    airportTaxes +
    accommodationsTaxes +
    additionalRequestsTaxes +
    insuranceTaxes +
    handlingTaxes
  );

  const totalPayable = round2(
    toursPayable +
    attractionsPayable +
    transfersPayable +
    flightsPayable +
    airportPayable +
    accommodationsPayable +
    additionalRequestsPayable +
    insurancePayable +
    handlingPayable
  );
  
  return {
    flights: { count: proposal.proposal_flights?.length || 0, base: flightsBase, taxes: flightsTaxes, payable: flightsPayable },
    transfers: { count: proposal.proposal_transfers?.length || 0, base: transfersBase, taxes: transfersTaxes, payable: transfersPayable },
    tours: { count: proposal.proposal_tours?.length || 0, base: toursBase, taxes: toursTaxes, payable: toursPayable },
    attractions: { count: proposal.proposal_attractions?.length || 0, base: attractionsBase, taxes: attractionsTaxes, payable: attractionsPayable },
    accommodations: { count: proposal.proposal_accommodations?.length || 0, base: accommodationsBase, taxes: accommodationsTaxes, payable: accommodationsPayable },
    airport: { count: proposal.proposal_airport_services?.length || 0, base: airportBase, taxes: airportTaxes, payable: airportPayable },
    additionalRequests: { count: proposal.proposal_additional_requests?.length || 0, base: additionalRequestsBase, taxes: additionalRequestsTaxes, payable: additionalRequestsPayable },
    insurance: { count: proposal.proposal_insurance?.length || 0, base: insuranceBase, taxes: insuranceTaxes, payable: insurancePayable },
    handlingFee: {
      base: handlingBase,
      taxes: handlingTaxes,
      payable: handlingPayable,
    },
    totalBase,
    totalTaxes,
    totalPayable,
  };
};

/**
 * Returns the pre-adjustment, pre-tax base amount for a single service item.
 * Mirrors what `calculateServiceTotals` would compute internally but with no
 * markup/discount applied — useful for per-item display in step cards.
 */
export const computeItemBaseAmount = (
  item: ServiceItem,
  service: PricedService | undefined,
  proposalData: { clients?: any; pax_adults?: number; pax_children?: number; pax_infants?: number; price_tier_override?: string | null; priceTierOverride?: string | null },
  options?: { taxPercentageOverride?: number },
): number => {
  const priceKey = getClientPriceKey(resolveEffectiveTier(proposalData));
  const paxCountRaw =
    (proposalData.pax_adults || 0) + (proposalData.pax_children || 0) + (proposalData.pax_infants || 0);
  const paxCount = Math.max(1, paxCountRaw);
  const result = calculateServiceTotals(item, service, priceKey, paxCount, 1, options?.taxPercentageOverride);
  return result.base;
};

export const computeProposalFinancialSummary = (
  proposal: ProposalData,
  options?: { pricingView?: ProposalPricingView | string | null }
): FinancialBreakdown => {
  const adjustment = getProposalPricingAdjustment(proposal, options?.pricingView);
  const adjusted = computeProposalFinancialSummaryForFactor(proposal, adjustment.factor);
  const base = adjustment.factor === 1
    ? adjusted
    : computeProposalFinancialSummaryForFactor(proposal, 1);

  return {
    ...adjusted,
    totalBaseBeforeAdjustment: base.totalBase,
    totalTaxesBeforeAdjustment: base.totalTaxes,
    totalPayableBeforeAdjustment: base.totalPayable,
    pricingAdjustmentType: adjustment.type,
    pricingAdjustmentPercentage: adjustment.percentage,
    pricingAdjustmentAmount: round2(adjusted.totalBase - base.totalBase),
    pricingAdjustmentTaxAmount: round2(adjusted.totalTaxes - base.totalTaxes),
    pricingAdjustmentTotalImpact: round2(adjusted.totalPayable - base.totalPayable),
  };
};

export const formatMoney = (
  value: number | null | undefined,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  const converted = convertUsdToCurrency(value, normalizedCurrency);
  return new Intl.NumberFormat(locale, { 
    style: 'currency', 
    currency: normalizedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(converted);
};
